import React, { PureComponent } from 'react'
import {
  Modal,
  Descriptions,
  Tag,
  Button,
  Spin,
  notification,
  Select,
  InputNumber,
  Form,
} from 'antd'
import moment from 'moment'
import Request, { API_URL } from '../../../request'
import MediaViewer from '../../../components/MediaViewer'
import styles from './styles.less'

const { Option } = Select

const fullUrl = (u) => {
  if (!u) return ''
  if (u.indexOf('http') === 0) return u
  return API_URL + (u.charAt(0) === '/' ? '' : '/') + u
}

const thumbStyle = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' }

const parseMedia = (data) => {
  if (!data) return { photos: [], videos: [] }
  if (Array.isArray(data)) {
    const photos = []
    const videos = []
    data.forEach((m) => {
      const type = (m && m.type) || ''
      if (type === 'video') videos.push(m)
      else if (type === 'image' || type === 'photo') photos.push(m)
      else photos.push(m)
    })
    return { photos, videos }
  }
  return {
    photos: data.photos || data.images || [],
    videos: data.videos || [],
  }
}

const emptyStockModal = {
  visible: false,
  mediaId: null,
  productId: undefined,
  productName: '',
  quantity: undefined,
  unit: '',
  saving: false,
  itemOrderId: '',
  itemUser: '',
  itemSubCategory: '',
  itemType: '',
}

// Outward review is driven by Capture Date — a click on a date group shows all
// outward media captured that day, regardless of Order ID.
class OutwardReviewModal extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      mediaLoading: false,
      photos: [],
      videos: [],
      products: [],
      stock: {}, // mediaId -> { saved, productName, quantity, unit }
      stockModal: { ...emptyStockModal },
      formMap: {}, // inwardId -> dispatch (outward form) record
      poForms: {}, // outward form id -> { loading, list } linked production orders
    }
    this.dirty = false
  }

  componentDidMount() {
    this.fetchProducts()
  }

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible && this.props.group) {
      this.dirty = false
      this.setState({ stock: {}, stockModal: { ...emptyStockModal } })
      this.fetchMedia()
    }
  }

  // Products come from the stock-product master:
  // GET https://sales-r.ht-india.com/stockProduct?results=2000
  fetchProducts = () => {
    const pickList = (res) =>
      !res ? [] : Array.isArray(res) ? res : res.data || res.products || []
    Request.getStockProductMaster({ results: 2000 }).then((res) => {
      this.setState({ products: pickList(res) })
    })
  }

  fetchMedia = () => {
    const { group } = this.props
    if (!group || !group.key) return
    const startDate = moment(group.key, 'YYYY-MM-DD').startOf('day').toISOString()
    const endDate = moment(group.key, 'YYYY-MM-DD').endOf('day').toISOString()
    this.setState({ mediaLoading: true, photos: [], videos: [] })
    Request.outwardMediaByDate({
      captureDate: group.key,
      startDate,
      endDate,
      orderIds: (group.orderIds || []).join(','),
      _t: Date.now(),
    }).then(({ error, data }) => {
      if (!error) {
        const { photos, videos } = parseMedia(data)
        const stock = {}
        ;[...photos, ...videos].forEach((m) => {
          if (m && m._id && m.movedToStock) {
            stock[m._id] = {
              saved: true,
              productName: m.stockProductName,
              quantity: m.stockQuantity,
              unit: m.stockUnit,
            }
          }
        })
        this.setState({ mediaLoading: false, photos, videos, stock })
        this.fetchForms([...photos, ...videos])
      } else {
        this.setState({ mediaLoading: false })
        notification.error({ message: 'Failed to load media' })
      }
    })
  }

  // fetch the outward (dispatch) form record for each photo's inwardId so the
  // attached form details can be shown in the image preview
  fetchForms = (items) => {
    const ids = []
    ;(items || []).forEach((p) => {
      if (p && p.inwardId && ids.indexOf(String(p.inwardId)) === -1) {
        ids.push(String(p.inwardId))
      }
    })
    ids.forEach((id) => {
      if (this.state.formMap[id]) return
      Request.getDispatchInwardOutwardById({ _id: id }).then(({ data, error }) => {
        if (!error && data) {
          this.setState((s) => ({ formMap: { ...s.formMap, [id]: data } }))
          if (data.productionOrderFormIds && data.productionOrderFormIds.length) {
            this.fetchLinkedPOs(id, data.productionOrderFormIds)
          }
        }
      })
    })
  }

  // linked production orders (+ their production images) for an outward form
  fetchLinkedPOs = (formId, ids) => {
    if (!ids || !ids.length || this.state.poForms[formId]) return
    this.setState((s) => ({ poForms: { ...s.poForms, [formId]: { loading: true, list: [] } } }))
    Request.productionFormsWithMedia({ ids: ids.map(String).join(',') })
      .then((res) => {
        const list = (res && res.data) || []
        this.setState((s) => ({ poForms: { ...s.poForms, [formId]: { loading: false, list } } }))
      })
      .catch(() => {
        this.setState((s) => ({ poForms: { ...s.poForms, [formId]: { loading: false, list: [] } } }))
      })
  }

  renderLinkedPOs = (formId) => {
    const entry = this.state.poForms[formId]
    if (!entry) return null
    if (entry.loading) {
      return (
        <div style={{ marginTop: 14 }}>
          <Spin size="small" /> <span style={{ marginLeft: 6, color: '#999' }}>Loading production orders…</span>
        </div>
      )
    }
    const list = entry.list || []
    if (!list.length) return null
    return (
      <div style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>
          Linked Production Orders
        </div>
        {list.map((po) => (
          <div
            key={po._id}
            style={{ marginBottom: 12, border: '1px solid #f0f0f0', borderRadius: 6, padding: 8 }}>
            <div style={{ marginBottom: 6 }}>
              <b>PO {po.productionOrderId}</b>
              {po.product ? <span style={{ color: '#555' }}> · {po.product}</span> : null}
              {po.priority ? (
                <Tag color={po.priority === 'High' ? 'red' : 'blue'} style={{ marginLeft: 6 }}>
                  {po.priority}
                </Tag>
              ) : null}
              {po.quantity != null ? (
                <Tag style={{ marginLeft: 4 }}>
                  Qty: {po.quantity}
                  {po.unitOfMeasurement ? ' ' + po.unitOfMeasurement : ''}
                </Tag>
              ) : null}
            </div>
            {po.images && po.images.length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {po.images.map((im) => (
                  <div
                    key={im._id}
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: 4,
                      overflow: 'hidden',
                      border: '1px solid #e8e8e8',
                      background: '#000',
                    }}>
                    {im.type === 'video' ? (
                      <video src={fullUrl(im.url)} style={thumbStyle} muted preload="metadata" />
                    ) : (
                      <img src={fullUrl(im.url)} style={thumbStyle} alt="" loading="lazy" />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#bbb', fontSize: 12 }}>No production images</div>
            )}
          </div>
        ))}
      </div>
    )
  }

  // details table of the outward form attached to the currently previewed image
  renderOutwardDetails = (current) => {
    const m = (current && current.raw) || {}
    const id = m.inwardId ? String(m.inwardId) : null
    const d = id ? this.state.formMap[id] : null
    if (!d) return null
    const products = d.products || []
    return (
      <div>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>
          Outward Form Details
        </div>
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Serial No.">{d.serialNumber || '-'}</Descriptions.Item>
          <Descriptions.Item label="Type">{d.type || '-'}</Descriptions.Item>
          <Descriptions.Item label="Department">{d.department || '-'}</Descriptions.Item>
          <Descriptions.Item label="Purpose">{d.purpose || '-'}</Descriptions.Item>
          <Descriptions.Item label="Challan Number">{d.challanNumber || '-'}</Descriptions.Item>
          <Descriptions.Item label="Consignee">{d.consignee || '-'}</Descriptions.Item>
          <Descriptions.Item label="Carrier">{d.carrier || '-'}</Descriptions.Item>
          <Descriptions.Item label="Date">
            {d.date ? moment(d.date).format('DD-MM-YYYY') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Products">
            {products.length
              ? products.map((p, i) => {
                  const qty =
                    p.quantity != null
                      ? p.quantity
                      : p.quantityRecieved != null
                      ? p.quantityRecieved
                      : p.quantityAccepted
                  return (
                    <div key={i} style={{ marginBottom: 2 }}>
                      {p.productName || '-'}
                      {qty != null && qty !== '' ? (
                        <span style={{ color: '#888' }}> · Qty: {qty}</span>
                      ) : null}{' '}
                      <Tag
                        color={p.subjectedToStock ? 'green' : ''}
                        style={{ marginLeft: 4 }}
                      >
                        Stock: {p.subjectedToStock ? 'Yes' : 'No'}
                      </Tag>
                    </div>
                  )
                })
              : '-'}
          </Descriptions.Item>
        </Descriptions>
        {this.renderLinkedPOs(id)}
      </div>
    )
  }

  deleteMedia = async (item) => {
    if (!item || !item._id) return
    const res = await Request.deleteOutwardMedia({ mediaId: item._id })
    if (res && !res.error) {
      notification.success({ message: 'Media deleted' })
      this.dirty = true
      this.setState((s) => ({
        photos: s.photos.filter((p) => String(p._id) !== String(item._id)),
        videos: s.videos.filter((p) => String(p._id) !== String(item._id)),
      }))
    } else {
      notification.error({ message: (res && res.message) || 'Failed to delete media' })
    }
  }

  handleItemReview = async (item, status, remarks) => {
    if (!item || !item._id) {
      notification.error({ message: 'Missing media id' })
      throw new Error('missing media id')
    }
    let res
    if (status === 'Rejected') {
      res = await Request.rejectOutwardMedia({ mediaId: item._id, remarks: remarks || '' })
    } else {
      res = await Request.completeOutwardMedia({ mediaId: item._id })
    }
    if (res.error) {
      notification.error({ message: res.message || 'Failed to update media' })
      throw new Error('review failed')
    }
    notification.success({ message: res.message || `Media marked as ${status}` })
    this.dirty = true
    return status
  }

  handleClose = () => {
    const { onUpdated, onClose } = this.props
    if (this.dirty && onUpdated) onUpdated()
    this.dirty = false
    if (onClose) onClose()
  }

  // Open the dedicated "Deleted Outward Images" page for this capture-date group
  // in a new tab (a full page handles many images and the restore confirm better)
  openDeletedPage = () => {
    const { group } = this.props
    const qs = []
    if (group && group.key) qs.push(`captureDate=${encodeURIComponent(group.key)}`)
    if (group && group.orderIds && group.orderIds.length) {
      qs.push(`orderIds=${encodeURIComponent(group.orderIds.join(','))}`)
    }
    window.open(`/outwardOrders/deleted${qs.length ? `?${qs.join('&')}` : ''}`, '_blank')
  }

  // ---- Move To Stock (modal) ----
  openStock = (item) => {
    const m = (item && item.raw) || {}
    if (!this.state.products.length) this.fetchProducts()
    this.setState({
      stockModal: {
        ...emptyStockModal,
        visible: true,
        mediaId: item._id,
        itemOrderId: m.orderId || '',
        itemUser: m.userName || '',
        itemSubCategory: m.subCategory || '',
        itemType: item.type || '',
      },
    })
  }

  closeStock = () => {
    this.setState({ stockModal: { ...emptyStockModal } })
  }

  setStockField = (patch) => {
    this.setState((s) => ({ stockModal: { ...s.stockModal, ...patch } }))
  }

  selectStockProduct = (productId) => {
    const product = this.state.products.find((p) => p._id === productId)
    this.setStockField({
      productId,
      productName: product ? product.name : '',
      unit: product ? product.unit || '' : '',
    })
  }

  saveStock = async () => {
    const { mediaId, productId, productName, quantity, unit } = this.state.stockModal
    if (!productId) {
      notification.error({ message: 'Please select a product' })
      return
    }
    if (!quantity || Number(quantity) <= 0) {
      notification.error({ message: 'Please enter a valid quantity' })
      return
    }
    this.setStockField({ saving: true })
    // 1. Existing Stock Outward API — creates the real stock-movement entry
    const res = await Request.addInward({
      recordDate: moment().toISOString(),
      type: 'Outward',
      totalData: [
        { productId, product: productName, quantity, description: '' },
      ],
    })
    if (!res.error) {
      // 2. Audit on the photo doc so the "In stock" tag persists across reloads
      await Request.moveOutwardMediaToStock({ mediaId, productId, productName, quantity, unit })

      notification.success({ message: res.message || 'Moved to stock' })
      this.setState((s) => ({
        stock: {
          ...s.stock,
          [mediaId]: { saved: true, productName, quantity, unit },
        },
        stockModal: { ...emptyStockModal },
      }))
      this.dirty = true
      // refresh this screen's data (modal media + the list behind it)
      this.fetchMedia()
      if (this.props.onUpdated) this.props.onUpdated()
    } else {
      notification.error({ message: res.message || 'Failed to move to stock' })
      this.setStockField({ saving: false })
    }
  }

  renderItemExtra = (item) => {
    const m = (item && item.raw) || {}
    const saved = this.state.stock[item._id]
    return (
      <div>
        {!m.inwardId ? (
          <Tag color="blue" style={{ marginBottom: 6 }}>
            Not Approved
          </Tag>
        ) : null}
        {(m.orderId || m.userName || m.subCategory) && (
          <div className={styles.itemMeta}>
            {m.orderId ? <span className={styles.itemMetaPart}>Order #{m.orderId}</span> : null}
            {m.userName ? <span className={styles.itemMetaPart}>{m.userName}</span> : null}
            {m.subCategory ? <span className={styles.itemMetaPart}>{m.subCategory}</span> : null}
          </div>
        )}

        {item.status === 'Completed' && (
          <div className={styles.moveToStock}>
            {saved && saved.saved ? (
              <Tag color="green" className={styles.stockSavedTag}>
                In stock: {saved.productName}
                {saved.quantity ? ` × ${saved.quantity}` : ''}
              </Tag>
            ) : (
              <Button
                size="small"
                icon="plus"
                type="primary"
                ghost
                onClick={() => this.openStock(item)}
              >
                Move To Stock
              </Button>
            )}
          </div>
        )}
      </div>
    )
  }

  renderStockModal = () => {
    const { products, stockModal } = this.state
    const {
      visible,
      productId,
      quantity,
      saving,
      itemOrderId,
      itemUser,
      itemSubCategory,
      itemType,
    } = stockModal
    return (
      <Modal
        title="Move To Stock"
        visible={visible}
        onCancel={this.closeStock}
        onOk={this.saveStock}
        confirmLoading={saving}
        okText="Save to Stock"
        destroyOnClose
        zIndex={1200}
      >
        <p style={{ color: '#888', marginBottom: 12 }}>
          Add this approved {itemType || 'item'} into stock inventory. Select the
          matching product and enter the quantity.
        </p>

        <Descriptions size="small" column={1} bordered className={styles.stockDetails}>
          {itemOrderId ? (
            <Descriptions.Item label="Order ID">#{itemOrderId}</Descriptions.Item>
          ) : null}
          {itemUser ? (
            <Descriptions.Item label="Uploaded By">{itemUser}</Descriptions.Item>
          ) : null}
          {itemSubCategory ? (
            <Descriptions.Item label="Subcategory">{itemSubCategory}</Descriptions.Item>
          ) : null}
          <Descriptions.Item label="Media Type">{itemType || '-'}</Descriptions.Item>
        </Descriptions>

        <Form layout="vertical" style={{ marginTop: 14 }}>
          <Form.Item label="Product" required>
            <Select
              showSearch
              allowClear
              placeholder="Select product"
              value={productId}
              optionFilterProp="children"
              style={{ width: '100%' }}
              dropdownStyle={{ zIndex: 1300 }}
              getPopupContainer={(trigger) => trigger.parentNode}
              onChange={this.selectStockProduct}
            >
              {products.map((p) => (
                <Option key={p._id} value={p._id}>
                  {p.name}
                  {p.productCode ? ` (${p.productCode})` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Quantity" required>
            <InputNumber
              min={1}
              placeholder="Quantity"
              value={quantity}
              style={{ width: '100%' }}
              onChange={(v) => this.setStockField({ quantity: v })}
            />
          </Form.Item>
        </Form>
      </Modal>
    )
  }

  render() {
    const { visible, group } = this.props
    const { mediaLoading, photos, videos } = this.state

    if (!group) return null

    const photosCount = photos.length
    const videosCount = videos.length

    return (
      <Modal
        visible={visible}
        onCancel={this.handleClose}
        footer={null}
        width={'100%'}
        style={{ top: 0, maxWidth: '100%', margin: 0, paddingBottom: 0 }}
        bodyStyle={{ height: 'calc(100vh - 55px)', overflowY: 'auto', padding: '16px 24px' }}
        wrapClassName={styles.fullModalWrap}
        title={
          <span>
            Outward Media — {group.label}{' '}
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {group.mediaCount || photosCount + videosCount} Media
            </Tag>
          </span>
        }
        destroyOnClose
      >
        <Descriptions
          size={'small'}
          column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
          bordered
        >
          <Descriptions.Item label={<b>Capture Date</b>}>{group.label}</Descriptions.Item>
          <Descriptions.Item label={<b>Orders</b>}>{group.orderCount || '-'}</Descriptions.Item>
          <Descriptions.Item label={<b>Photos</b>}>{photosCount}</Descriptions.Item>
          <Descriptions.Item label={<b>Videos</b>}>{videosCount}</Descriptions.Item>
        </Descriptions>

        <div style={{ marginTop: 12, color: '#888' }}>
          Showing all outward photos &amp; videos captured on <b>{group.label}</b>.
          Mark each item <b>Complete</b> or <b>Reject</b> (with a reason).
          Approved items can be moved into stock inventory.
        </div>

        <div style={{ marginTop: 12 }}>
          {mediaLoading ? (
            <div className={styles.mediaLoading}>
              <Spin tip="Loading media..." />
            </div>
          ) : (
            <MediaViewer
              reviewable
              rejectWithReason
              photos={photos}
              videos={videos}
              onReview={this.handleItemReview}
              onDelete={this.deleteMedia}
              renderItemExtra={this.renderItemExtra}
              detailsPanel={this.renderOutwardDetails}
              isLocked={(item) =>
                !!(
                  (this.state.stock[item._id] && this.state.stock[item._id].saved) ||
                  (item.raw && item.raw.movedToStock)
                )
              }
            />
          )}
        </div>

        <div className={styles.modalFooter} style={{ textAlign: 'center' }}>
          <Button icon="delete" onClick={this.openDeletedPage} style={{ marginRight: 8 }}>
            Deleted Images
          </Button>
          <Button onClick={this.handleClose}>Close</Button>
        </div>

        {this.renderStockModal()}
      </Modal>
    )
  }
}

export default OutwardReviewModal
